import React, { useState, useEffect } from 'react';

interface ChatMessage {
  id: string;
  message: string;
  response: string;
  timestamp: string;
  quality_score?: number;
  needs_review?: boolean;
}

interface TrainingItem {
  id: string;
  question: string;
  ideal_answer?: string;
  status: string;
  created_at: string;
}

const ChatTrainingDashboard = () => {
  const [chats, setChats] = useState<ChatMessage[]>([]);
  const [training, setTraining] = useState<TrainingItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedChat, setSelectedChat] = useState<ChatMessage | null>(null);
  const [stats, setStats] = useState({
    totalChats: 0,
    needsReview: 0,
    avgQuality: 0,
    trainingQueue: 0
  });

  // Fetch chat history from D1
  const fetchChats = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://futr-bet-api.b-9f2.workers.dev/api/training/chats');
      const data = await response.json();

      if (data.success) {
        setChats(data.chats || []);
        setStats(prev => ({
          ...prev,
          totalChats: data.chats?.length || 0,
          needsReview: data.chats?.filter((c: ChatMessage) => c.needs_review).length || 0,
          avgQuality: data.avgQuality || 0
        }));
      }
    } catch (error) {
      console.error('Failed to fetch chats:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch training queue
  const fetchTraining = async () => {
    try {
      const response = await fetch('https://futr-bet-api.b-9f2.workers.dev/api/training/queue');
      const data = await response.json();

      if (data.success) {
        setTraining(data.items || []);
        setStats(prev => ({
          ...prev,
          trainingQueue: data.items?.length || 0
        }));
      }
    } catch (error) {
      console.error('Failed to fetch training queue:', error);
    }
  };

  useEffect(() => {
    fetchChats();
    fetchTraining();
  }, []);

  // Score a chat conversation
  const scoreChat = async (chatId: string, score: number) => {
    try {
      const response = await fetch('https://futr-bet-api.b-9f2.workers.dev/api/training/score', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId, score })
      });

      if (response.ok) {
        fetchChats(); // Refresh
      }
    } catch (error) {
      console.error('Failed to score chat:', error);
    }
  };

  // Add to training queue
  const addToTraining = async (chat: ChatMessage) => {
    try {
      const response = await fetch('https://futr-bet-api.b-9f2.workers.dev/api/training/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: chat.message,
          ideal_answer: chat.response,
          source: 'dashboard'
        })
      });

      if (response.ok) {
        fetchTraining();
        alert('Added to training queue!');
      }
    } catch (error) {
      console.error('Failed to add to training:', error);
    }
  };

  return (
    <div className="min-h-screen bg-black text-green-400 font-mono p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
            🔧 Chat Training Dashboard
            <span className="text-sm text-yellow-400">ENGINEER MODE</span>
          </h1>
          <p className="text-gray-400">How our AI gets smarter with every conversation</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <div className="bg-gray-900 border border-green-400 rounded p-4">
            <div className="text-3xl font-bold">{stats.totalChats}</div>
            <div className="text-sm text-gray-400">Total Conversations</div>
          </div>
          <div className="bg-gray-900 border border-yellow-400 rounded p-4">
            <div className="text-3xl font-bold text-yellow-400">{stats.needsReview}</div>
            <div className="text-sm text-gray-400">Needs Review</div>
          </div>
          <div className="bg-gray-900 border border-green-400 rounded p-4">
            <div className="text-3xl font-bold">{stats.avgQuality.toFixed(1)}/5</div>
            <div className="text-sm text-gray-400">Avg Quality Score</div>
          </div>
          <div className="bg-gray-900 border border-blue-400 rounded p-4">
            <div className="text-3xl font-bold text-blue-400">{stats.trainingQueue}</div>
            <div className="text-sm text-gray-400">Training Queue</div>
          </div>
        </div>

        {/* How It Works */}
        <div className="bg-gray-900 border border-green-400 rounded-lg p-6 mb-8">
          <h2 className="text-2xl font-bold mb-4 text-green-300">🧠 How Our AI Learns</h2>

          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="border border-gray-700 rounded p-3">
              <div className="text-yellow-400 font-bold mb-2">1. CAPTURE</div>
              <p>Every chat is stored in D1 database with:</p>
              <ul className="mt-2 space-y-1 text-xs">
                <li>• User message</li>
                <li>• AI response</li>
                <li>• Timestamp</li>
                <li>• Chat ID</li>
              </ul>
            </div>

            <div className="border border-gray-700 rounded p-3">
              <div className="text-yellow-400 font-bold mb-2">2. ANALYZE</div>
              <p>We track quality metrics:</p>
              <ul className="mt-2 space-y-1 text-xs">
                <li>• Response time</li>
                <li>• Token usage</li>
                <li>• User feedback</li>
                <li>• Follow-up questions</li>
              </ul>
            </div>

            <div className="border border-gray-700 rounded p-3">
              <div className="text-yellow-400 font-bold mb-2">3. IMPROVE</div>
              <p>Good answers become training data:</p>
              <ul className="mt-2 space-y-1 text-xs">
                <li>• Add to vector DB</li>
                <li>• Update prompts</li>
                <li>• Refine responses</li>
                <li>• Learn patterns</li>
              </ul>
            </div>
          </div>

          <div className="mt-4 p-3 bg-black rounded border border-green-400">
            <code className="text-xs">
              <span className="text-gray-500">// When user asks a new question:</span><br/>
              <span className="text-yellow-400">1.</span> Convert to embedding → [0.23, -0.45, 0.67, ...]<br/>
              <span className="text-yellow-400">2.</span> Search vector DB for similar past questions<br/>
              <span className="text-yellow-400">3.</span> Use best matches as context for AI<br/>
              <span className="text-yellow-400">4.</span> Generate better, more relevant response<br/>
              <span className="text-yellow-400">5.</span> Store new Q&A pair for future learning
            </code>
          </div>
        </div>

        {/* Recent Chats */}
        <div className="grid grid-cols-2 gap-6">
          {/* Chat List */}
          <div className="bg-gray-900 border border-green-400 rounded-lg p-4">
            <h3 className="text-xl font-bold mb-4 flex justify-between">
              Recent Conversations
              <button
                onClick={fetchChats}
                className="text-xs bg-green-600 text-white px-2 py-1 rounded"
              >
                Refresh
              </button>
            </h3>

            {loading ? (
              <div className="text-center py-8 text-gray-400">Loading chats...</div>
            ) : chats.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                No chats yet. Start chatting to see data!
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {chats.map(chat => (
                  <div
                    key={chat.id}
                    onClick={() => setSelectedChat(chat)}
                    className={`p-3 border rounded cursor-pointer transition-colors ${
                      selectedChat?.id === chat.id
                        ? 'border-yellow-400 bg-gray-800'
                        : 'border-gray-700 hover:border-green-400'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <div className="text-xs text-gray-500">{chat.id}</div>
                      {chat.needs_review && (
                        <span className="text-xs text-yellow-400">⚠️ Review</span>
                      )}
                    </div>
                    <div className="text-sm text-green-400 truncate">
                      Q: {chat.message}
                    </div>
                    <div className="text-xs text-gray-400 truncate">
                      A: {chat.response}
                    </div>
                    <div className="flex justify-between mt-2">
                      <span className="text-xs text-gray-600">
                        {new Date(chat.timestamp).toLocaleTimeString()}
                      </span>
                      {chat.quality_score && (
                        <span className="text-xs text-yellow-400">
                          ⭐ {chat.quality_score}/5
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Chat Detail */}
          <div className="bg-gray-900 border border-green-400 rounded-lg p-4">
            <h3 className="text-xl font-bold mb-4">Chat Analysis</h3>

            {selectedChat ? (
              <div className="space-y-4">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Chat ID</div>
                  <div className="font-mono text-xs text-yellow-400">{selectedChat.id}</div>
                </div>

                <div>
                  <div className="text-xs text-gray-500 mb-1">User Message</div>
                  <div className="bg-black p-3 rounded border border-gray-700 text-sm">
                    {selectedChat.message}
                  </div>
                </div>

                <div>
                  <div className="text-xs text-gray-500 mb-1">AI Response</div>
                  <div className="bg-black p-3 rounded border border-gray-700 text-sm">
                    {selectedChat.response}
                  </div>
                </div>

                <div>
                  <div className="text-xs text-gray-500 mb-2">Quality Score</div>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map(score => (
                      <button
                        key={score}
                        onClick={() => scoreChat(selectedChat.id, score)}
                        className={`px-3 py-1 rounded border ${
                          selectedChat.quality_score === score
                            ? 'bg-yellow-400 text-black border-yellow-400'
                            : 'border-gray-600 hover:border-yellow-400'
                        }`}
                      >
                        {score}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => addToTraining(selectedChat)}
                    className="flex-1 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                  >
                    Add to Training Queue
                  </button>
                  <button
                    className="flex-1 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                  >
                    Flag for Review
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                Select a chat to analyze
              </div>
            )}
          </div>
        </div>

        {/* Training Queue */}
        <div className="mt-8 bg-gray-900 border border-blue-400 rounded-lg p-4">
          <h3 className="text-xl font-bold mb-4 text-blue-400">📚 Training Queue</h3>

          {training.length === 0 ? (
            <div className="text-center py-4 text-gray-400">No items in training queue</div>
          ) : (
            <div className="space-y-2">
              {training.map(item => (
                <div key={item.id} className="flex justify-between items-center p-3 bg-black rounded border border-gray-700">
                  <div className="flex-1">
                    <div className="text-sm">{item.question}</div>
                    {item.ideal_answer && (
                      <div className="text-xs text-gray-400 mt-1">→ {item.ideal_answer}</div>
                    )}
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${
                    item.status === 'approved' ? 'bg-green-600' :
                    item.status === 'rejected' ? 'bg-red-600' :
                    'bg-yellow-600'
                  } text-white`}>
                    {item.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-gray-500">
          <p>Every conversation makes our AI smarter. This is how we learn.</p>
          <p className="mt-1">🚀 Powered by CloudFlare AI + D1 + Vectorize (coming soon)</p>
        </div>
      </div>
    </div>
  );
};

export default ChatTrainingDashboard;